/** A tenant's **AI vocabulary** (client MCP plan §12.5 "How ops see the changes"; backend WPS
 *  P2-10): the company's own words for our concepts — "LATAM" for some of its regions, "Tour
 *  Leader" for its guides — read ONLY by the AI's knowledge tools (`lookup_terms`,
 *  `describe_tenant`, `resolve_value`, the analytics breakdowns). Nothing a guest sees changes,
 *  and never the product's guide role.
 *
 *  - `GET /tour_operators/{id}/ai_vocabulary/` (`console_ai_vocabulary_view`) — every entry (the
 *    company's and support's first, then those derived from its set-up) with its source, binding
 *    and the names it binds, its last change (objchange) and the vocabulary change set that last
 *    wrote it; the tenant's latest 50 vocabulary change sets; the guide-role findings;
 *    `can_edit` (the caller holds `console_ai_vocabulary_manage` and change sets are on).
 *  - `POST` the same URL — ops' edit **as support** (source `ops`): create, update (the company's
 *    entry becomes support's) or delete, through the same proposal, preview and checks as the MCP
 *    tool `define_term`, applied at once as ONE revertible change set — on production too, never
 *    pending. A derived entry is never edited (400 `invalid`).
 *  - `GET / POST …/changes/{change_set_id}/revert/` — the dry run of reverting one of the tenant's
 *    vocabulary change sets (`AIChangeRevertPlan`, `?skips=`) and the revert, now and never
 *    forced (forcing over conflicts stays the AI changes page's).
 *
 *  Shapes come from the generated spec types; this module adds the query keys and the words. */
import { REVERT_HTTP_TIMEOUT_MS, normalizeSkips, type AIChangeRevertPlan } from './aiChanges';
import { apiGet, apiPost, extendedListConfig } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type ConsoleAIVocabulary = Schemas['ConsoleAIVocabulary'];
export type VocabularyEntry = Schemas['ConsoleVocabularyEntry'];
export type VocabularyBinding = Schemas['ConsoleVocabularyBinding'];
export type VocabularyBindingNames = Schemas['ConsoleVocabularyBindingNames'];
export type VocabularyConcept = ConsoleAIVocabulary['concepts'][number];
export type VocabularyEdit = Schemas['ConsoleAIVocabularyEdit'];
export type VocabularyEditFields = NonNullable<VocabularyEdit['fields']>;
export type VocabularyEditResult = Schemas['ConsoleAIVocabularyEditResult'];
export type VocabularyRevert = Schemas['ConsoleAIVocabularyRevert'];
export type VocabularyRevertResult = Schemas['ConsoleAIVocabularyRevertResult'];
export type VocabularyError = Schemas['ConsoleAIVocabularyError'];
export type VocabularyOrigin = VocabularyEntry['origin'];
/** A finding (`vocabulary.guide_role_mismatch`): free-form in the spec, read defensively. */
export type VocabularyFinding = ConsoleAIVocabulary['findings'][number];

/** The edit's limits (`ConsoleAIVocabularyEdit`; the contract test keeps them equal). */
export const VOCABULARY_LIMITS = {
  term: 200,
  definition: 1000,
  aliases: 20,
  alias: 200,
  reasonMin: 3,
  reasonMax: 4000,
} as const;

const base = (topId: number) => `/tour_operators/${topId}/ai_vocabulary/`;
const revertPath = (topId: number, changeSetId: string) =>
  `${base(topId)}changes/${encodeURIComponent(changeSetId)}/revert/`;

export function vocabulary(topId: number, timeout?: 'extended'): Promise<ConsoleAIVocabulary> {
  return apiGet(`${base(topId)}${timeout ? '?timeout=extended' : ''}`, extendedListConfig(timeout));
}

/** Applies at once (200 `ConsoleAIVocabularyEditResult`). Refused: 400 (DRF field errors, or
 *  `code: invalid` with `errors`), 404 (no such entry of this tenant), 409 (`change_sets_disabled`
 *  or the change engine's gates) — `vocabularyErrorOf` reads the coded bodies. */
export function editEntry(topId: number, body: VocabularyEdit): Promise<VocabularyEditResult> {
  return apiPost(base(topId), body);
}

export function revertPlan(
  topId: number,
  changeSetId: string,
  skips: readonly number[],
): Promise<AIChangeRevertPlan> {
  const s = normalizeSkips(skips);
  return apiGet(`${revertPath(topId, changeSetId)}${s.length ? `?skips=${s.join(',')}` : ''}`, {
    timeout: REVERT_HTTP_TIMEOUT_MS,
  });
}

/** Reverts now (200). 409: `stale_plan`, `revert_blocked`, `revert_refused`, `revert_failed`
 *  (rolled back), `change_sets_disabled`. */
export function revert(
  topId: number,
  changeSetId: string,
  body: VocabularyRevert,
): Promise<VocabularyRevertResult> {
  return apiPost(revertPath(topId, changeSetId), body, { timeout: REVERT_HTTP_TIMEOUT_MS });
}

export const vocabularyKeys = {
  all: ['aiVocabulary'] as const,
  tab: (topId: number) => ['aiVocabulary', topId] as const,
  revertPlan: (topId: number, changeSetId: string, skips: readonly number[]) =>
    ['aiVocabulary', topId, 'revertPlan', changeSetId, normalizeSkips(skips).join(',')] as const,
};

/** A coded refusal (`{code, detail, errors?}`), or null for anything else. */
export function vocabularyErrorOf(body: unknown): VocabularyError | null {
  const b = body as { code?: unknown; detail?: unknown } | null | undefined;
  return typeof b?.code === 'string' && typeof b.detail === 'string' ? (b as VocabularyError) : null;
}

/** DRF field errors (`{reason: ["…"], "fields.term": "…"}`) as "field: message" lines. */
export function fieldErrorsOf(body: unknown): string[] {
  if (!body || typeof body !== 'object' || 'code' in body) return [];
  const out: string[] = [];
  for (const [key, value] of Object.entries(body as Record<string, unknown>)) {
    const messages = Array.isArray(value) ? value : [value];
    for (const m of messages) if (typeof m === 'string') out.push(key === 'detail' ? m : `${key}: ${m}`);
  }
  return out;
}

// ---- words -----------------------------------------------------------------------

export const ORIGIN_LABELS: Record<VocabularyOrigin, string> = {
  tenant: 'Company',
  ops: 'Support',
  auto: 'Derived',
};

export const ORIGIN_HELP: Record<VocabularyOrigin, string> = {
  tenant: 'Written by the company (its dashboard, or its AI with define_term).',
  ops: 'Written by our support for the company (this tab).',
  auto: 'Derived from the company’s set-up and rewritten whenever it changes: never edited. Add an entry of its own for the word instead — the AI reads it first.',
};

/** The binding keys in the order the tab shows them (the contract test checks it names every
 *  key of `ConsoleVocabularyBinding`). */
export const BINDING_KEYS = [
  'role_ids',
  'region_ids',
  'countries',
  'tag_category',
  'booking_system',
  'channel',
  'language',
  'default',
] as const satisfies readonly (keyof VocabularyBinding)[];

export const BINDING_LABELS: Record<(typeof BINDING_KEYS)[number], string> = {
  role_ids: 'Staff roles',
  region_ids: 'Regions',
  countries: 'Countries',
  tag_category: 'Tag category',
  booking_system: 'Booking system',
  channel: 'Channel',
  language: 'Language',
  default: 'Default language',
};

/** "Staff roles: Tour Leader, Driver" — one line per key the binding carries. Ids no longer the
 *  tenant's own have no name and show as `#id`. */
export function bindingLines(
  binding: VocabularyBinding,
  names: VocabularyBindingNames | undefined,
): string[] {
  const out: string[] = [];
  const named = (ids: number[] | undefined, list: { id: number; name: string }[] | undefined) =>
    (ids ?? []).map((id) => list?.find((n) => n.id === id)?.name ?? `#${id}`).join(', ');
  for (const key of BINDING_KEYS) {
    const value = binding[key];
    if (value === undefined || value === null || value === '') continue;
    if (Array.isArray(value) && value.length === 0) continue;
    let text: string;
    if (key === 'role_ids') text = named(binding.role_ids, names?.role_ids);
    else if (key === 'region_ids') text = named(binding.region_ids, names?.region_ids);
    else if (key === 'countries') text = (binding.countries ?? []).join(', ');
    else if (key === 'default') {
      if (!binding.default) continue;
      text = 'yes';
    } else text = String(value);
    out.push(`${BINDING_LABELS[key]}: ${text}`);
  }
  return out;
}

/** A binding without its empty keys (what the edit sends and compares). */
export function compactBinding(binding: VocabularyBinding): VocabularyBinding {
  const out: VocabularyBinding = {};
  for (const key of BINDING_KEYS) {
    const value = binding[key];
    if (value === undefined || value === null || value === '' || value === false) continue;
    if (Array.isArray(value) && value.length === 0) continue;
    (out as Record<string, unknown>)[key] = value;
  }
  return out;
}

/** A change set the tab can revert: applied, or partially reverted. */
export const REVERTABLE_STATUSES = ['applied', 'partially_reverted'] as const;
